import { useState } from 'react';
import type { ProjectInfo } from '@niuma/protocol';

interface Props {
  projects: ProjectInfo[];
  projectRoot: string;
  selected: string | null;
  busy: boolean;
  onSelect: (name: string) => void;
  onCreate: (name: string, initGit: boolean, addReadme: boolean) => void;
  onRefresh: () => void;
}

/** Lists projects under the machine's project root and creates new ones. */
export function ProjectPicker({
  projects,
  projectRoot,
  selected,
  busy,
  onSelect,
  onCreate,
  onRefresh,
}: Props): JSX.Element {
  const [name, setName] = useState('');
  const [initGit, setInitGit] = useState(true);
  const [addReadme, setAddReadme] = useState(true);
  const [creating, setCreating] = useState(false);

  const submit = (): void => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate(trimmed, initGit, addReadme);
    setName('');
    setCreating(false);
  };

  return (
    <section className="panel">
      <header className="panel-header">
        <div>
          <p className="eyebrow">choose</p>
          <h2>Projects</h2>
          <code className="panel-path">{projectRoot}</code>
        </div>
        <div className="panel-actions">
          <button className="ghost" onClick={onRefresh} disabled={busy}>
            Refresh
          </button>
          <button className="primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'New'}
          </button>
        </div>
      </header>

      {creating && (
        <div className="new-project">
          <input
            value={name}
            placeholder="project-name"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
          <label>
            <input
              type="checkbox"
              checked={initGit}
              onChange={(e) => setInitGit(e.target.checked)}
            />
            git init
          </label>
          <label>
            <input
              type="checkbox"
              checked={addReadme}
              onChange={(e) => setAddReadme(e.target.checked)}
            />
            README
          </label>
          <button className="primary" onClick={submit} disabled={busy || !name.trim()}>
            Create
          </button>
        </div>
      )}

      <ul className="project-list">
        {projects.map((project) => (
          <li key={project.name} className={project.name === selected ? 'active' : ''}>
            <button className="row-main" onClick={() => onSelect(project.name)}>
              <span className="row-title">{project.name}</span>
              <span className="row-meta">{project.isGitRepo ? 'Git repository' : 'Folder'}</span>
            </button>
            {project.isGitRepo && <span className="badge">git</span>}
          </li>
        ))}
        {projects.length === 0 && <li className="muted">No projects yet.</li>}
      </ul>
    </section>
  );
}
